/**
 * Правка плана агроцикла на шкале: чистая часть (без React и сети).
 *
 * Шкала `WindowTimeline` сообщает, что человек хотел («окно работы стало
 * таким»). Здесь это превращается в срок-правило работы (`timing`), каким его
 * принимает сервер, и проверяется тем же расчётом окон, что на сервере
 * (`planWindows`, копия `work.plan_windows`): работы, которые отсчитываются от
 * сдвинутой, едут вместе с ней ещё до ответа сервера.
 *
 * Запущенная работа (по ней есть кампания) на шкале не сохраняется: её срок
 * меняет решение кампании «Перенести» (S5). Шкала только собирает новые даты.
 */

import type { Schemas } from '../../../api/client';
import type { BarDrag } from '../../ui/timeline/WindowTimeline';
import type { DayWindow, ShiftKind } from '../../ui/timeline/windowMath';
import { canonicalTiming, dragOfTiming, planWindows, timingAfterShift, type TimingLike } from '../template/templateDraft';
import type { DeskCampaign } from '../ops/campaigns/desk';
import type { PlanStage, PlanWork } from './agrocycleApi';

export type CycleTiming = Schemas['CycleTimingIO'];

/** Кампания закрыта: срок такой работы уже не переносят. */
const CLOSED_CAMPAIGN = new Set(['completed', 'cancelled']);

/** Срок работы цикла в виде, общем с шаблоном: опорная работа там зовётся `start_after_id`. */
export function timingLikeOf(t: CycleTiming): TimingLike {
  return { ...t, start_after_id: t.start_after ?? null };
}

/** Канонический срок → тело запроса правки работы цикла. */
export function cycleTimingOf(t: TimingLike): CycleTiming {
  const c = canonicalTiming(t);
  return {
    start_kind: c.start_kind as CycleTiming['start_kind'],
    start_md: c.start_md,
    start_year_offset: c.start_year_offset,
    start_after: c.start_after_id,
    start_after_edge: c.start_after_edge as CycleTiming['start_after_edge'],
    start_after_days: c.start_after_days,
    end_kind: c.end_kind as CycleTiming['end_kind'],
    end_md: c.end_md,
    end_year_offset: c.end_year_offset,
    duration_days: c.duration_days,
    approximate: c.approximate,
  };
}

/** По работе идёт или шла кампания. */
export const isLaunched = (work: PlanWork): boolean => work.campaign_id != null;

/** Кампания открыта: срок можно перенести решением. */
export const canReschedule = (work: PlanWork): boolean =>
  work.campaign_id != null && !CLOSED_CAMPAIGN.has(work.campaign_status ?? '') && work.state !== 'cancelled';

/**
 * Как работу можно двигать на шкале. Запущенную тоже тащат (за любой край):
 * шкала соберёт новые даты, а экран откроет «Перенести» с ними. Незапущенную —
 * по форме её срока (даты — целиком и за края, отсчёт от другой — целиком).
 */
export function workDrag(work: PlanWork, hasWindow: boolean): BarDrag {
  if (!hasWindow || work.state === 'cancelled') return 'fixed';
  if (work.campaign_id != null) return canReschedule(work) ? 'free' : 'fixed';
  return dragOfTiming(timingLikeOf(work.timing), true);
}

export type PlanTimings = Map<number, TimingLike>;

/** Сроки всех работ плана — вход расчёта окон. */
export function planTimings(stages: ReadonlyArray<PlanStage>): PlanTimings {
  const map: PlanTimings = new Map();
  for (const stage of stages) for (const work of stage.works) map.set(work.id, timingLikeOf(work.timing));
  return map;
}

export interface ShiftPlan {
  /** Новый срок работы — тело запроса. */
  timing: CycleTiming;
  /** Срок до правки — для «Отменить». */
  previous: CycleTiming;
  /** Окна работы и всех, кто от неё отсчитывается и ещё не запущен: id работы → новое окно. */
  windows: Map<number, DayWindow>;
}

export type ShiftResult = { ok: true; plan: ShiftPlan } | { ok: false; reason: 'shape' | 'range' };

/**
 * Что значит «окно работы стало `next`»: новый срок и новые окна зависимых.
 * `shape` — так срок задать нельзя (29 февраля, год вне «до / посадки / после»,
 * форма срока на шкале не двигается); `range` — расчёт окон не сходится
 * (конец раньше начала, круг опор).
 */
export function planShift(
  stages: ReadonlyArray<PlanStage>,
  anchorYear: number,
  workId: number,
  prev: DayWindow,
  next: DayWindow,
  kind: ShiftKind,
): ShiftResult {
  const work = stages.flatMap((s) => s.works).find((w) => w.id === workId);
  if (!work) return { ok: false, reason: 'shape' };
  const like = timingAfterShift(timingLikeOf(work.timing), anchorYear, prev, next, kind);
  if (!like) return { ok: false, reason: 'shape' };
  const timings = planTimings(stages);
  timings.set(workId, like);
  const computed = planWindows(timings, anchorYear);
  if (!computed) return { ok: false, reason: 'range' };
  const windows = new Map<number, DayWindow>();
  windows.set(workId, next);
  for (const stage of stages) {
    for (const other of stage.works) {
      if (other.id === workId || other.campaign_id != null) continue; // запущенную двигает только решение кампании
      const win = computed.get(other.id) ?? null;
      if (!win) continue;
      if (win.from !== other.window_from || win.to !== other.window_to) windows.set(other.id, win);
    }
  }
  return { ok: true, plan: { timing: cycleTimingOf(like), previous: cycleTimingOf(timingLikeOf(work.timing)), windows } };
}

/** Строка кампании для окна решения «Перенести»: берёт только то, что окну нужно, остальное — пустые итоги. */
export function campaignOf(work: PlanWork, window: DayWindow): DeskCampaign {
  return {
    id: work.campaign_id ?? 0,
    title: work.label,
    status: work.campaign_status ?? 'active',
    work_id: work.id,
    work_label: work.label,
    window_from: window.from,
    window_to: window.to ?? window.from,
    obligations: 0,
    cancelled: 0,
    farms: 0,
    outside_fields: 0,
    departed: 0,
    decisions: [],
  };
}

/** Ключ строки шкалы по работе плана и обратно. */
export const barKey = (workId: number): string => `p${workId}`;
export function workIdOfBar(key: string): number | null {
  if (!key.startsWith('p')) return null;
  const id = Number(key.slice(1));
  return Number.isInteger(id) ? id : null;
}

export function findWork(stages: ReadonlyArray<PlanStage> | undefined, workId: number): { work: PlanWork; stage: PlanStage } | null {
  for (const stage of stages ?? []) {
    const work = stage.works.find((w) => w.id === workId);
    if (work) return { work, stage };
  }
  return null;
}
